// Missions Alpine.js component

import { missionsService } from './missions.service.js';
import { trackMissionEvent } from '../analytics/fathom.js';
import { queueApiCall, PRIORITY } from '../utils/init-queue.js';
import { shouldLoadProtectedData, hasToken } from '../utils/startup.js';

export const missions = () => ({
  // State
  availableMissions: [],
  activeRun: null,
  loading: false,
  error: null,
  isOffline: false,
  
  // Initialization guards
  _initialized: false,
  _loadingMissions: false,
  _checkingRun: false,
  
  // Initialize component
  init() {
    if (this._initialized) {
      console.warn('Missions component already initialized');
      return;
    }
    
    console.log('Missions component initialized');
    this._initialized = true;
    
    // Only load if authenticated or has token
    if (hasToken()) {
      this.loadMissions();
      this.checkActiveRun();
    } else {
      console.log('⏳ Missions: Waiting for authentication before loading data');
    }
    
    // Check if offline
    this.isOffline = !navigator.onLine;
    window.addEventListener('online', () => {
      this.isOffline = false;
      this.refresh();
    });
    window.addEventListener('offline', () => {
      this.isOffline = true;
    });
    
    // Listen for timer updates
    window.addEventListener('time-update', () => {
      this.checkActiveRun();
    });
    
    // Listen for timer completion
    window.addEventListener('timer-complete', (event) => {
      if (event.detail.timerType === 'mission') {
        this.onMissionComplete();
      }
    });
    
    // Listen for logout to cleanup
    window.addEventListener('app:logout', () => this.destroy());
  },
  
  // Load available missions for current city
  async loadMissions() {
    if (this._loadingMissions) return; // Prevent concurrent calls
    
    // Check if should load protected data
    if (!shouldLoadProtectedData('missions')) {
      console.log('⏳ Missions: Not authenticated or feature disabled');
      return;
    }
    
    this._loadingMissions = true;
    this.loading = true;
    this.error = null;
    
    try {
      const currentCity = this.$store.app.p().location || 'town';
      
      // Try cache first if offline
      if (this.isOffline) {
        const cached = await this.getCachedMissions(currentCity);
        if (cached) {
          this.availableMissions = cached;
          console.log(`Loaded ${cached.length} missions from cache for ${currentCity}`);
          return;
        }
      }
      
      // Queue API call to prevent overwhelming the server
      this.availableMissions = await queueApiCall(
        () => missionsService.list(currentCity),
        PRIORITY.NORMAL
      );
      
      // Cache for offline use
      this.cacheMissions(currentCity, this.availableMissions);
      
      console.log(`Loaded ${this.availableMissions.length} missions for ${currentCity}`);
    } catch (error) {
      console.error('Failed to load missions:', error);
      
      // Try cache fallback
      try {
        const currentCity = this.$store.app.p().location || 'town';
        const cached = await this.getCachedMissions(currentCity);
        if (cached) {
          this.availableMissions = cached;
          this.error = 'Usando datos sin conexión';
          return;
        }
      } catch (cacheError) {
        console.warn('Cache fallback failed:', cacheError);
      }
      
      this.error = 'Error al cargar las misiones';
    } finally {
      this.loading = false;
      this._loadingMissions = false;
    }
  },
  
  // Check for active mission run
  async checkActiveRun() {
    if (this._checkingRun) return; // Prevent concurrent calls
    
    // Check if should load protected data
    if (!shouldLoadProtectedData('missions')) {
      console.log('⏳ Missions: Not authenticated or feature disabled');
      return null;
    }
    
    this._checkingRun = true;
    try {
      // Queue API call with higher priority (active mission is important)
      this.activeRun = await queueApiCall(
        () => missionsService.getActiveRun(),
        PRIORITY.HIGH
      );
      
      if (this.activeRun) {
        // Update timer display
        this.$nextTick(() => {
          this.updateTimerDisplay();
        });
      }
    } catch (error) {
      console.error('Failed to check active mission run:', error);
    } finally {
      this._checkingRun = false;
    }
  },
  
  // Accept a mission
  async acceptMission(missionId) {
    if (this.loading || this.activeRun || this.isOffline) return;
    
    this.loading = true;
    this.error = null;
    
    try {
      const run = missionsService.accept(missionId);
      this.activeRun = run;
      
      // Analytics tracking
      trackMissionEvent('accept', missionId);
      
      // Show success notification
      this.$store.app.addNotification(
        `Misión aceptada: ${run.mission.title}`, 
        'success'
      );
      
      console.log('Mission accepted:', run);
      
    } catch (error) {
      console.error('Failed to accept mission:', error);
      this.error = error.message;
      this.$store.app.addNotification(error.message, 'error');
    } finally {
      this.loading = false;
    }
  },
  
  // Claim mission rewards
  async claimRewards() {
    if (!this.activeRun || this.activeRun.status !== 'completed' || this.isOffline) return;
    
    this.loading = true;
    this.error = null;
    
    try {
      // Use queueMicrotask to ensure Alpine store is available
      await new Promise(resolve => queueMicrotask(resolve));
      
      const result = missionsService.claim(this.activeRun.runId, this.$store.app);
      
      // Analytics tracking
      trackMissionEvent('claim', this.activeRun.missionId);
      
      // Show result notification
      const message = result.success 
        ? `¡Misión exitosa! +${result.reward.coins} monedas`
        : `Misión parcialmente fallida. +${result.reward.coins} monedas`;
      
      const type = result.success ? 'success' : 'warning';
      this.$store.app.addNotification(message, type);
      
      // Clear active run and refresh
      this.activeRun = null;
      
      // Refresh component state after successful claim
      setTimeout(() => {
        this.refresh();
      }, 0);
      
      console.log('Mission claimed:', result);
      
    } catch (error) {
      console.error('Failed to claim mission:', error);
      this.error = error.message;
      
      // Fallback: try to refresh after error to sync state
      setTimeout(() => {
        this.refresh();
      }, 100);
      
      this.$store.app.addNotification(error.message, 'error');
    } finally {
      this.loading = false;
    }
  },
  
  // Cancel active mission
  async cancelMission() {
    if (!this.activeRun || this.loading || this.isOffline) return;
    
    if (!confirm('¿Estás seguro de que quieres cancelar la misión?')) {
      return;
    }
    
    try {
      missionsService.cancel();
      this.activeRun = null;
      
      this.$store.app.addNotification('Misión cancelada', 'info');
      console.log('Mission cancelled');
      
    } catch (error) {
      console.error('Failed to cancel mission:', error);
      this.error = error.message;
    }
  },
  
  // Handle mission completion (called by timer)
  onMissionComplete() {
    this.checkActiveRun();
    
    if (this.activeRun && this.activeRun.status === 'completed') {
      this.$store.app.addNotification(
        `¡Misión completada: ${this.activeRun.mission.title}!`, 
        'success'
      );
    }
  },
  
  // Update timer display
  updateTimerDisplay() {
    if (!this.activeRun || this.activeRun.status !== 'active') return;
    
    const element = this.$refs.missionTimer;
    if (!element) return;
    
    const timeLeft = this.activeRun.endsAt - Date.now();
    
    if (timeLeft <= 0) {
      element.textContent = 'Completada';
      element.classList.remove('pulse');
    } else {
      const seconds = Math.ceil(timeLeft / 1000);
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      
      element.textContent = `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      
      // Add pulse effect when less than 30 seconds
      if (timeLeft < 30000) {
        element.classList.add('pulse');
      } else {
        element.classList.remove('pulse');
      }
    }
  },
  
  // Check if mission requirements are met
  canAcceptMission(mission) {
    if (!this.$store.app.hasPlayer()) return false;
    const player = this.$store.app.p();
    
    if (!mission.requirements) {
      return true;
    }

    // Handle both array and object requirements
    const requirements = Array.isArray(mission.requirements) 
      ? mission.requirements 
      : Object.entries(mission.requirements || {}).map(([type, value]) => ({ type, value }));
    
    if (requirements.length === 0) {
      return true;
    }
    
    return requirements.every(req => {
      switch (req.type) {
        case 'level':
          return player.level >= req.value;
        case 'health':
          return player.health >= req.value;
        case 'money':
          return player.money >= req.value;
        case 'item':
          // This would check inventory when implemented
          return true;
        default:
          return true;
      }
    });
  },
  
  // Get requirements text for display
  getRequirementsText(mission) {
    if (!mission.requirements) {
      return 'Sin requisitos';
    }

    // Handle both array and object requirements
    const requirements = Array.isArray(mission.requirements) 
      ? mission.requirements 
      : Object.entries(mission.requirements || {}).map(([type, value]) => ({ type, value }));
    
    if (requirements.length === 0) {
      return 'Sin requisitos';
    }
    
    return requirements.map(req => {
      switch (req.type) {
        case 'level':
          return `Nivel ${req.value}`;
        case 'health':
          return `Salud ${req.value}%`;
        case 'money':
          return `${req.value} monedas`;
        case 'item':
          return `${req.quantity}x ${req.id}`;
        default:
          return `${req.type}: ${req.value}`;
      }
    }).join(', ');
  },
  
  // Get tier styling
  getTierClass(tier) {
    const tierMap = {
      'EASY': 'tier-easy',
      'MEDIUM': 'tier-medium', 
      'HARD': 'tier-hard',
      'EPIC': 'tier-epic'
    };
    return tierMap[tier] || 'tier-unknown';
  },
  
  // Get tier info
  getTierInfo(tier) {
    return missionsService.getTierInfo(tier);
  },
  
  // Format time duration
  formatDuration(minutes) {
    if (minutes < 60) {
      return `${minutes}m`;
    } else {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
  },
  
  // Format end time for display
  formatEndTime() {
    if (!this.activeRun) return '';
    
    const endDate = new Date(this.activeRun.endsAt);
    return endDate.toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  },
  
  // Check if can start missions (not traveling, not KO'd)
  get canStartMissions() {
    const store = this.$store.app;
    
    if (!store.hasPlayer()) return false;
    
    // Can't start if traveling
    if (store.timers?.travel) {
      return false;
    }
    
    // Can't start if KO'd
    if (store.p().health <= 0) {
      return false;
    }
    
    // Can't start if already have active mission
    if (this.activeRun && this.activeRun.status === 'active') {
      return false;
    }
    
    return true;
  },
  
  // Get block reason message
  get blockReason() {
    const store = this.$store.app;
    
    if (!store.hasPlayer()) return 'Login requerido';
    
    if (store.timers?.travel) {
      return 'No puedes hacer misiones mientras viajas';
    }
    
    if (store.p().health <= 0) {
      return 'No puedes hacer misiones estando inconsciente';
    }
    
    if (this.activeRun && this.activeRun.status === 'active') {
      return 'Ya tienes una misión activa';
    }
    
    return '';
  },

  // Helper to get mission reward coins (compatibility layer)
  getMissionRewardCoins(mission) {
    return mission.rewardCoins || mission.reward?.coins || 0;
  },

  // Helper to get mission duration text
  formatDuration(minutes) {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  },
  
  // Refresh missions (reload from service)
  refresh() {
    this.loadMissions();
    this.checkActiveRun();
  },
  
  // Cache missions for offline use
  async cacheMissions(city, missions) {
    try {
      if ('SimpleReadCache' in window) {
        await window.SimpleReadCache.set(`missions_${city}`, missions, { ttl: 300000 }); // 5 min TTL
      }
    } catch (error) {
      console.warn('Failed to cache missions:', error);
    }
  },
  
  // Get cached missions
  async getCachedMissions(city) {
    try {
      if ('SimpleReadCache' in window) {
        return await window.SimpleReadCache.get(`missions_${city}`);
      }
    } catch (error) {
      console.warn('Failed to get cached missions:', error);
    }
    return null;
  },
  
  // Cleanup when component is destroyed
  destroy() {
    this._initialized = false;
    this._loadingMissions = false;
    this._checkingRun = false;
    console.log('Missions component destroyed');
  }
});